import { User } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import * as React from 'react'

import { useTenantMediaDefaults } from '@/hooks/use-tenant-media-defaults'

import type { LandingInstructor } from '@/components/homepage/non-fsds-landing-content'

interface NonFsdsInstructorCardProps {
  instructor: LandingInstructor
  isEnglish: boolean
}

function resolveExternalHref(url: string): string | null {
  if (!url) {
    return null
  }

  try {
    const parsed = new URL(url)
    const isValidProtocol = parsed.protocol === 'https:'
    const host = parsed.hostname.toLowerCase()
    const isLinkedinHost = host === 'linkedin.com' || host === 'www.linkedin.com' || host.endsWith('.linkedin.com')

    if (!isValidProtocol || !isLinkedinHost) {
      return null
    }

    return parsed.href
  } catch {
    return null
  }
}

export default function NonFsdsInstructorCard({ instructor, isEnglish }: NonFsdsInstructorCardProps) {
  const { resolveAvatarUrl } = useTenantMediaDefaults()
  const linkedinHref = resolveExternalHref(instructor.linkedin)
  const avatarSrc = resolveAvatarUrl(instructor.avatar)

  return (
    <article
      data-testid="non-fsds-instructor-card"
      className="group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-blue-100/80 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-100/50 sm:p-8"
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-white to-blue-50/30" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-blue-300 to-sky-300 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

      <div className="relative flex flex-col items-center text-center space-y-6">
        <div className="relative mb-2 size-20 shrink-0 overflow-hidden rounded-full shadow-sm ring-4 ring-white">
          {avatarSrc ? (
            <Image
              src={avatarSrc}
              alt={instructor.username}
              width={80}
              height={80}
              className="relative block size-full object-cover"
            />
          ) : (
            <div className="flex size-full items-center justify-center bg-slate-100 text-slate-400">
              <User className="size-8" aria-hidden="true" />
            </div>
          )}
        </div>
        <div>
          <h3 className="line-clamp-2 text-xl font-bold leading-tight tracking-tight text-slate-900">
            {instructor.username}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm font-semibold leading-snug text-blue-600/90">
            {instructor.position}
          </p>
        </div>
      </div>

      <div className="relative mx-auto mt-6 h-px w-12 bg-blue-200" />

      <p className="relative mt-6 line-clamp-4 text-sm leading-relaxed text-slate-600">{instructor.description}</p>

      <div className="relative mt-auto flex items-end justify-between gap-4 border-t border-blue-100/60 pt-6">
        <span className="line-clamp-2 max-w-[65%] text-[0.82rem] font-bold uppercase tracking-wide text-slate-400">
          {isEnglish ? instructor.course : instructor.module}
        </span>

        {linkedinHref ? (
          <Link
            href={linkedinHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-full bg-blue-50 px-4 text-sm font-bold text-blue-700 transition-all duration-200 hover:bg-blue-600 hover:text-white hover:shadow-md"
          >
            <svg className="size-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            LinkedIn
          </Link>
        ) : (
          <span
            aria-disabled="true"
            className="inline-flex min-h-10 shrink-0 cursor-not-allowed items-center justify-center gap-2 rounded-full bg-slate-50 px-4 text-sm font-bold text-slate-400"
          >
            <svg className="size-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            LinkedIn
          </span>
        )}
      </div>
    </article>
  )
}
